import {PinIcon} from '@primer/octicons-react'
import type {ReactElement} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'
import type {ReaderFragment} from 'relay-runtime'
import {getFragmentIdentifier} from 'relay-runtime'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueCommentPinnedEvent$key} from './__generated__/IssueCommentPinnedEvent.graphql'
import type {IssueCommentUnpinnedEvent$key} from './__generated__/IssueCommentUnpinnedEvent.graphql'
import {IssueCommentCopyRendering, IssueCommentLink} from './IssueCommentPinShared'
import {RolledupIssueCommentPinEvent} from './RolledupIssueCommentPinEvent'
import {TimelineRow} from './row/TimelineRow'

type IssueCommentUnpinnedEventProps = {
  queryRef: IssueCommentUnpinnedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: Record<string, Array<IssueCommentUnpinnedEvent$key | IssueCommentPinnedEvent$key>>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const issueCommentUnpinnedEventFragment = graphql`
  fragment IssueCommentUnpinnedEvent on IssueCommentUnpinnedEvent {
    databaseId
    createdAt
    actor {
      avatarUrl(size: 64)
      login
      profileResourcePath
      __typename
      ... on Bot {
        isCopilot
        isAgent
        displayName
      }
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
    }
    issueComment {
      url
      databaseId
    }
  }
`

const issueCommentUnpinnedEventFragmentNode = issueCommentUnpinnedEventFragment as unknown as ReaderFragment

const getUnpinnedFragmentIdentifier = (queryRef: IssueCommentUnpinnedEvent$key) =>
  getFragmentIdentifier(issueCommentUnpinnedEventFragmentNode, queryRef)

export function IssueCommentUnpinnedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: IssueCommentUnpinnedEventProps): React.ReactElement {
  const {actor, createdAt, databaseId} = useFragment(issueCommentUnpinnedEventFragment, queryRef)

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PinIcon}
    >
      <TimelineRow.Main>
        <span data-testid="issue-comment-unpinned-copy">
          {rollupGroup ? (
            <RolledupIssueCommentPinEvent rollupGroup={rollupGroup} />
          ) : (
            <UnpinnedCommentRendering queryRefs={[queryRef]} rollup={false} />
          )}
        </span>{' '}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

type UnpinnedCommentRenderingProps = {
  queryRefs: IssueCommentUnpinnedEvent$key[]
  rollup: boolean
}

export const UnpinnedCommentRendering = ({queryRefs, rollup}: UnpinnedCommentRenderingProps): ReactElement | null => {
  return (
    <IssueCommentCopyRendering
      queryRefs={queryRefs}
      rollup={rollup}
      label={LABELS.timeline.issueCommentUnpinned.leading}
      getFragmentIdentifier={getUnpinnedFragmentIdentifier}
      renderLink={queryRef => <UnpinnedCommentLink queryRef={queryRef} />}
      linkPrefix="a "
    />
  )
}

export const UnpinnedCommentLink = ({queryRef}: {queryRef: IssueCommentUnpinnedEvent$key}): ReactElement => {
  const data = useFragment(issueCommentUnpinnedEventFragment, queryRef)
  return (
    <IssueCommentLink
      linkText={LABELS.timeline.issueCommentUnpinned.comment}
      url={data.issueComment?.url}
      testId="issue-comment-unpinned-link"
    />
  )
}
